import React from "react";
import { Box, Typography, Button, Stack, Container } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Lock, Smartphone, Palette, ArrowRight } from "lucide-react";
import { heroData } from "../data/vizapData";

const HeroContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  minHeight: "100vh",
  position: "relative",
  display: "flex",
  alignItems: "center",
  backgroundColor: "transparent",
  overflow: "hidden",
  paddingTop: "80px",
  paddingBottom: "60px",
  [theme.breakpoints.down("md")]: {
    minHeight: "auto",
    paddingTop: "60px",
    paddingBottom: "50px",
  },
  [theme.breakpoints.down("sm")]: {
    paddingTop: "40px",
    paddingBottom: "40px",
  },
}));

const iconMap = {
  Lock: Lock,
  Smartphone: Smartphone,
  Palette: Palette
};

const FeatureBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: { xs: "6px", md: "8px" },
  padding: { xs: "6px 12px", md: "8px 16px" },
  borderRadius: "100px",
  backdropFilter: "blur(10px)",
  background: "rgba(255, 255, 255, 0.1)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.15)",
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    marginBottom: "8px",
  },
}));

const GlowingButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #4674D9 0%, #3567D6 100%)",
  color: "#FFFFFF",
  padding: "14px 32px",
  fontSize: "16px",
  fontWeight: 600,
  borderRadius: "12px",
  textTransform: "none",
  boxShadow: "0 8px 32px rgba(70, 116, 217, 0.4)",
  border: "none",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  fontFamily: "'Poppins', sans-serif",
  "&:hover": {
    background: "linear-gradient(135deg, #3a5fb8 0%, #2c52b5 100%)",
    boxShadow: "0 12px 40px rgba(70, 116, 217, 0.5)",
    transform: "translateY(-2px)",
  },
}));

const HeroSection = () => {
  return (
    <HeroContainer>
      {/* Decorative gradient orbs */}
      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.4, 0.6, 0.4],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          top: "10%",
          right: "5%",
          width: { xs: "200px", sm: "300px", md: "500px" },
          height: { xs: "200px", sm: "300px", md: "500px" },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(70, 116, 217, 0.15) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
          display: { xs: "none", sm: "block" },
        }}
      />

      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 8, sm: 9, md: 6 }}
          alignItems="center"
        >
          {/* Left side - App Mockup with animated content */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            sx={{
              flex: { xs: 1, md: 0.9 },
              display: "flex",
              justifyContent: "center",
              position: "relative",
              order: { xs: 2, md: 1 },
              mt: { xs: 6, md: 0 },
              mb: { xs: 2, md: 0 },
            }}
          >
            <Box
              component={motion.div}
              animate={{
                y: [0, -15, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              sx={{
                position: "relative",
                width: { xs: "200px", sm: "240px", md: "300px" },
                height: { xs: "380px", sm: "450px", md: "540px" },
                mb: { xs: 1, md: 0 },
              }}
            >
              {/* Glow effect behind phone */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "130%",
                  height: "130%",
                  background: "radial-gradient(circle, rgba(70, 116, 217, 0.3) 0%, transparent 70%)",
                  filter: "blur(50px)",
                  zIndex: 0,
                }}
              />
              
              {/* Phone frame */}
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  background: "linear-gradient(145deg, #1a1a2e 0%, #0f0f1e 100%)",
                  borderRadius: { xs: "30px", md: "40px" },
                  border: { xs: "8px solid #2a2a3e", md: "10px solid #2a2a3e" },
                  boxShadow: "0 40px 80px rgba(0, 0, 0, 0.6), inset 0 0 0 1px rgba(255, 255, 255, 0.1)",
                  overflow: "hidden",
                  zIndex: 1,
                }}
              >
                {/* Notch */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "35%",
                    height: { xs: "20px", md: "26px" },
                    background: "#1a1a2e",
                    borderRadius: { xs: "0 0 12px 12px", md: "0 0 16px 16px" },
                    zIndex: 3,
                  }}
                />

                {/* Screen content with gradient background */}
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(180deg, #4674D9 0%, #2a5298 50%, #1a3d7a 100%)",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Animated background pattern */}
                  <Box
                    component={motion.div}
                    animate={{
                      backgroundPosition: ["0% 0%", "100% 100%"],
                    }}
                    transition={{
                      duration: 20,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `
                        radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)
                      `,
                      backgroundSize: "200% 200%",
                    }}
                  />

                  {/* App header with time and status */}
                  <Box sx={{ p: { xs: 1.5, md: 2 }, pt: { xs: 3, md: 4 }, position: "relative", zIndex: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: { xs: 1.5, md: 2 } }}>
                      <Typography sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: { xs: "9px", md: "11px" }, fontWeight: 600 }}>
                        9:41
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Box sx={{ width: { xs: "12px", md: "14px" }, height: { xs: "8px", md: "10px" }, border: { xs: "1px solid", md: "1.5px solid" }, borderColor: "rgba(255, 255, 255, 0.9)", borderRadius: "2px", position: "relative", opacity: 0 }}>
                          <Box sx={{ position: "absolute", right: "-3px", top: "50%", transform: "translateY(-50%)", width: { xs: "1.5px", md: "2px" }, height: { xs: "3px", md: "4px" }, background: "rgba(255, 255, 255, 0.9)", borderRadius: "0 1px 1px 0" }} />
                        </Box>
                      </Stack>
                    </Stack>
                    <Typography
                      sx={{
                        color: "#FFFFFF",
                        fontSize: { xs: "14px", sm: "16px", md: "20px" },
                        fontWeight: 700,
                        fontFamily: "'Poppins', sans-serif",
                        textAlign: "center",
                        mb: { xs: 0.3, md: 0.5 },
                      }}
                    >
                      Smart Health Report
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(255, 255, 255, 0.7)",
                        fontSize: { xs: "8px", sm: "9px", md: "11px" },
                        fontWeight: 400,
                        fontFamily: "'Poppins', sans-serif",
                        textAlign: "center",
                      }}
                    >
                      Report generated: Just now
                    </Typography>
                  </Box>

                  {/* Critical test results */}
                  <Stack
                    spacing={{ xs: 0.6, sm: 0.8, md: 1.2 }}
                    sx={{
                      px: { xs: 1.2, sm: 1.5, md: 2 },
                      flex: 1,
                      justifyContent: "center",
                      position: "relative",
                      zIndex: 2,
                      overflow: "hidden",
                    }}
                  >
                    {[
                      { 
                        label: "Blood Glucose", 
                        value: "92 mg/dL", 
                        range: "70-100",
                        status: "Normal",
                        color: "#4ECDC4", 
                        delay: 0,
                      },
                      { 
                        label: "Creatinine", 
                        value: "0.85 mg/dL", 
                        range: "0.6-1.2",
                        status: "Optimal",
                        color: "#95E1D3", 
                        delay: 0.12,
                      },
                      { 
                        label: "ALT (Liver)", 
                        value: "28 U/L", 
                        range: "<40",
                        status: "Normal",
                        color: "#4ECDC4", 
                        delay: 0.24,
                      },
                      { 
                        label: "WBC Count", 
                        value: "6.8 ×10³/μL", 
                        range: "4.0-11.0",
                        status: "Normal",
                        color: "#95E1D3", 
                        delay: 0.36,
                      },
                      { 
                        label: "Calcium", 
                        value: "9.6 mg/dL", 
                        range: "8.5-10.5",
                        status: "Good",
                        color: "#A78BFA", 
                        delay: 0.48,
                      },
                    ].map((item, index) => (
                      <Box
                        key={index}
                        component={motion.div}
                        initial={{ opacity: 0, x: -15 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: 0.5,
                          delay: item.delay,
                          repeat: Infinity,
                          repeatType: "reverse",
                          repeatDelay: 4,
                        }}
                        sx={{
                          background: "rgba(255, 255, 255, 0.12)",
                          backdropFilter: "blur(10px)",
                          borderRadius: { xs: "8px", md: "10px" },
                          p: { xs: 0.8, sm: 1, md: 1.2 },
                          border: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={{ xs: 0.8, md: 1 }}>
                          <Box flex={1}>
                            <Typography
                              sx={{
                                color: "#FFFFFF",
                                fontSize: { xs: "8px", sm: "9px", md: "10px" },
                                fontWeight: 600,
                                fontFamily: "'Poppins', sans-serif",
                                mb: { xs: 0.3, md: 0.5 },
                                lineHeight: 1.2,
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Stack direction="row" alignItems="baseline" spacing={{ xs: 0.3, md: 0.5 }}>
                              <Typography
                                sx={{
                                  color: "#FFFFFF",
                                  fontSize: { xs: "10px", sm: "11px", md: "13px" },
                                  fontWeight: 700,
                                  fontFamily: "'Poppins', sans-serif",
                                  lineHeight: 1,
                                }}
                              >
                                {item.value}
                              </Typography>
                              <Typography
                                sx={{
                                  color: "rgba(255, 255, 255, 0.6)",
                                  fontSize: { xs: "7px", sm: "7.5px", md: "8px" },
                                  fontWeight: 400,
                                  fontFamily: "'Poppins', sans-serif",
                                }}
                              >
                                ({item.range})
                              </Typography>
                            </Stack>
                          </Box>
                          <Stack alignItems="flex-end" spacing={{ xs: 0.2, md: 0.3 }}>
                            <Box
                              sx={{
                                width: { xs: "5px", md: "6px" },
                                height: { xs: "5px", md: "6px" },
                                borderRadius: "50%",
                                background: item.color,
                                boxShadow: `0 0 8px ${item.color}`,
                              }}
                            />
                            <Typography
                              sx={{
                                color: item.color,
                                fontSize: { xs: "6px", sm: "6.5px", md: "7px" },
                                fontWeight: 700,
                                fontFamily: "'Poppins', sans-serif",
                                textTransform: "uppercase",
                              }}
                            >
                              {item.status}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>

                  {/* Summary footer */}
                  <Box
                    sx={{
                      p: { xs: 1, sm: 1.2, md: 1.5 },
                      background: "rgba(0, 0, 0, 0.25)",
                      backdropFilter: "blur(10px)",
                      position: "relative",
                      zIndex: 2,
                      pb: { xs: 1.2, md: 1.5 },
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography
                          sx={{
                            color: "rgba(255, 255, 255, 0.7)",
                            fontSize: { xs: "7px", sm: "7.5px", md: "8px" },
                            fontWeight: 400,
                            fontFamily: "'Poppins', sans-serif",
                            mb: { xs: 0.2, md: 0.3 },
                          }}
                        >
                          Parameters Analyzed
                        </Typography>
                        <Typography
                          sx={{
                            color: "#FFFFFF",
                            fontSize: { xs: "11px", sm: "12px", md: "14px" },
                            fontWeight: 700,
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          48
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          px: { xs: 1.2, sm: 1.5, md: 2 },
                          py: { xs: 0.7, md: 1 },
                          background: "rgba(255, 255, 255, 0.2)",
                          borderRadius: { xs: "6px", md: "8px" },
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            background: "rgba(255, 255, 255, 0.3)",
                          }
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#FFFFFF",
                            fontSize: { xs: "8px", sm: "9px", md: "10px" },
                            fontWeight: 600,
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          View All →
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Right side - Content */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            sx={{ 
              flex: { xs: 1, md: 1.3 },
              display: "flex",
              alignItems: "center",
              width: "100%",
              justifyContent: { xs: "center", md: "flex-start" },
              order: { xs: 1, md: 2 },
              mb: { xs: 4, md: 0 },
              pl: { xs: 0, md: 4 },
            }}
          >
            <Stack spacing={{ xs: 2.5, sm: 3, md: 4 }} sx={{ width: "100%", maxWidth: { xs: "100%", md: "600px" } }}>
              {/* Title with gradient effect */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              >
                <Typography
                  sx={{
                    background: "linear-gradient(135deg, #4674D9 0%, #282D4D 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    fontSize: { xs: "36px", sm: "48px", md: "72px" },
                    fontWeight: 800,
                    lineHeight: { xs: 1.1, md: 1.1 },
                    fontFamily: "'SF Compact Display', sans-serif",
                    letterSpacing: { xs: "-0.5px", md: "-2px" },
                    pb: { xs: 0.5, md: 1 },
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  {heroData.title}
                </Typography>
              </motion.div>

              {/* Feature badges */}
              <Stack
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                direction="row"
                spacing={{ xs: 1, sm: 1.5, md: 2 }}
                flexWrap="wrap"
                sx={{ 
                  mt: { xs: 1, md: 2.5 },
                  justifyContent: { xs: "center", md: "flex-start" },
                }}
              >
                {heroData.features.map((feature, index) => {
                  const IconComponent = iconMap[feature.icon];
                  return (
                    <FeatureBadge key={index}>
                      {IconComponent && (
                        <Box sx={{ 
                          display: "flex", 
                          alignItems: "center",
                          "& svg": {
                            width: { xs: "17px", md: "20px" },
                            height: { xs: "17px", md: "20px" },
                            marginRight: "5px"
                          }
                        }}>
                          <IconComponent size={20} color="#4674D9" strokeWidth={2.5} />
                        </Box>
                      )}
                      <Typography
                        sx={{
                          color: "#4C4C4C",
                          fontSize: { xs: "12px", sm: "13px", md: "15px" },
                          fontWeight: 600,
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      >
                        {feature.text}
                      </Typography>
                    </FeatureBadge>
                  );
                })}
              </Stack>

              {/* Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
              >
                <Typography
                  sx={{
                    color: "#4C4C4C",
                    fontSize: { xs: "14px", sm: "15px", md: "18px" },
                    lineHeight: { xs: 1.6, md: 1.8 },
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 400,
                    textAlign: { xs: "center", md: "left" },
                    px: { xs: 1, md: 0 },
                    maxWidth: { xs: "100%", md: "540px" },
                  }}
                >
                  {heroData.description}
                </Typography>
              </motion.div>

              {/* CTA Button */}
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-start" },
                  width: "100%",
                }}
              >
                <GlowingButton
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  endIcon={<ArrowRight size={20} />}
                  onClick={() => {
                    window.open("https://www.demo-vizapp.niroggyan.com/overview", "_blank", "noopener,noreferrer");
                  }}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    fontSize: { xs: "14px", md: "16px" },
                    padding: { xs: "12px 24px", md: "14px 32px" },
                    marginBottom: "40px",
                    marginTop: "0px",
                  }}
                >
                  {heroData.ctaText}
                </GlowingButton>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </HeroContainer>
  );
};

export default HeroSection;